/**
 * Design System — LoadingState
 * Consistent loading indicator used across all modules.
 *
 * Usage:
 *   <LoadingState />
 *   <LoadingState message="Loading invoices..." />
 *   <LoadingState variant="skeleton" rows={5} />
 */
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface LoadingStateProps {
  message?: string;
  variant?: 'spinner' | 'skeleton';
  /** Number of skeleton rows (only for variant="skeleton") */
  rows?: number;
  className?: string;
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="w-8 h-8 bg-slate-200 dark:bg-slate-800 rounded-lg shrink-0 shimmer" />
      <div className="flex-1 space-y-2">
        <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded-full w-2/3 shimmer" />
        <div className="h-2 bg-slate-100 dark:bg-slate-800/60 rounded-full w-1/3 shimmer" />
      </div>
      <div className="h-5 w-16 bg-slate-200 dark:bg-slate-800 rounded-full shrink-0 shimmer" />
      <div className="h-3 w-20 bg-slate-100 dark:bg-slate-800/60 rounded shrink-0 shimmer" />
    </div>
  );
}

export default function LoadingState({ message = 'Loading...', variant = 'spinner', rows = 6, className }: LoadingStateProps) {
  if (variant === 'skeleton') {
    return (
      <div className={cn('flex-1 flex flex-col min-h-0', className)}>
        {/* Skeleton toolbar */}
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="h-8 bg-slate-200 dark:bg-slate-800 rounded-xl flex-1 max-w-xs shimmer" />
          <div className="h-8 w-24 bg-slate-200 dark:bg-slate-800 rounded-xl shimmer" />
          <div className="h-8 w-24 bg-slate-200 dark:bg-slate-800 rounded-xl shimmer" />
        </div>
        {/* Skeleton rows */}
        <div className="border-t border-slate-200 dark:border-slate-800">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 flex flex-col items-center justify-center py-20 space-y-4', className)}>
      <div className="relative">
        <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-800 rounded-full" />
        <Loader2 className="absolute top-0 left-0 w-12 h-12 text-primary animate-spin" />
      </div>
      <p className="text-sm font-bold text-slate-900 dark:text-white">{message}</p>
    </div>
  );
}
